class PureForm extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-initialized')) return
    this.setAttribute('data-initialized', '')

    this.form = this.querySelector('.pure-form__el')
    if (!this.form) return

    this.submitButton = this.querySelector('.pure-form__submit')
    this.successMessage = this.querySelector('.pure-form__msg--success')
    this.errorMessage = this.querySelector('.pure-form__msg--error[role="alert"]')
    this.conditionals = Array.from(this.querySelectorAll('[data-show-if-field]'))
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleChange = this.handleChange.bind(this)

    this.form.addEventListener('submit', this.handleSubmit)
    this.form.addEventListener('change', this.handleChange)
    this.syncConditionals()
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit)
    this.form?.removeEventListener('change', this.handleChange)
    this.removeAttribute('data-initialized')
  }

  handleChange() {
    this.syncConditionals()
  }

  syncConditionals() {
    for (const wrapper of this.conditionals) {
      const source = this.form.elements[wrapper.dataset.showIfField]
      const show = Boolean(source) && source.value === wrapper.dataset.showIfEquals
      wrapper.hidden = !show
      for (const input of wrapper.querySelectorAll('input, select, textarea')) {
        input.disabled = !show
      }
    }
  }

  setBusy(busy) {
    if (!this.submitButton) return
    this.submitButton.disabled = busy
    this.submitButton.querySelector('.pure-form__submit-label').hidden = busy
    this.submitButton.querySelector('.pure-form__submit-wait').hidden = !busy
  }

  showError(message) {
    this.errorMessage.textContent = message
    this.errorMessage.hidden = false
    this.errorMessage.focus({ preventScroll: true })
  }

  focusFirstInvalid(fieldErrors) {
    const firstName = Object.keys(fieldErrors || {})[0]
    if (!firstName) return
    const field = this.form.elements[firstName]
    const control = field instanceof RadioNodeList ? field[0] : field
    control?.focus?.()
  }

  fieldErrorMessage(fieldErrors) {
    const firstName = Object.keys(fieldErrors || {})[0]
    if (!firstName) return ''
    const field = this.form.elements[firstName]
    const control = field instanceof RadioNodeList ? field[0] : field
    const label = control?.labels?.[0]?.textContent?.replace(/\s*\*\s*$/, '').trim()
      || firstName.replaceAll('-', ' ')
    return `${label}: ${fieldErrors[firstName]}`
  }

  resetTurnstile() {
    if (window.turnstile) window.turnstile.reset()
  }

  async handleSubmit(event) {
    event.preventDefault()

    if (!this.form.checkValidity()) {
      this.form.reportValidity()
      return
    }

    this.errorMessage.hidden = true
    this.setBusy(true)

    try {
      const fields = new FormData(this.form)
      if (this.dataset.formKey === 'rso_application') {
        // Native date inputs expose ISO dates; the existing intake contract
        // accepts the original MM/DD/YYYY storefront representation.
        const date = fields.get('Date-of-Birth')
        const iso = typeof date === 'string' && date.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        if (iso) fields.set('Date-of-Birth', `${iso[2]}/${iso[3]}/${iso[1]}`)
      }
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fields,
      })

      const contentType = response.headers.get('content-type') || ''
      if (!contentType.includes('application/json')) throw new Error('unexpected-response')
      const result = await response.json()

      if (!response.ok || !result.ok) {
        if (response.status === 422 && Object.keys(result.fieldErrors || {}).length) {
          this.showError(this.fieldErrorMessage(result.fieldErrors))
          this.focusFirstInvalid(result.fieldErrors)
        } else if (response.status === 403) {
          this.showError('Please complete the verification and try again.')
        } else if (response.status === 429) {
          this.showError('Too many attempts. Please wait a moment and try again.')
        } else if (result.retriable) {
          this.showError('The form service is temporarily unavailable. Please try again.')
        } else {
          this.showError('We could not send your form. Please review it and try again.')
        }
        this.resetTurnstile()
        return
      }

      this.form.hidden = true
      this.successMessage.hidden = false
      this.successMessage.focus({ preventScroll: true })
      this.dispatchEvent(
        new CustomEvent('pure-form:submitted', {
          bubbles: true,
          detail: { formKey: this.dataset.formKey },
        })
      )
    } catch {
      this.showError(
        'We could not reach the form service. Please try again, or email forms@pureshenandoah.com.'
      )
      this.resetTurnstile()
    } finally {
      this.setBusy(false)
    }
  }
}

if (!customElements.get('pure-form')) customElements.define('pure-form', PureForm)
